import type { MascotConfig } from './mascot-catalog'
import type { MascotAppearance } from './mascot-appearance'
import { MascotDirt } from './MascotDetails'
import { MascotFace } from './MascotFace'
import styles from './DonorMascot.module.css'
import { MascotHeadwear, MascotPattern } from './MascotDecorations'

export const MASCOT_HEAD_PATH =
  'M51 98C48 72 68 51 94 49Q102 40 112 46Q123 37 133 47C166 45 191 65 190 97Q202 108 191 119L195 125 186 132 188 137C173 157 151 163 120 162S64 157 51 138L54 132 44 125 49 119Q38 110 51 98Z'

export const JAGUAR_HEAD_PATH =
  'M46 100C44 69 67 47 96 46Q120 39 145 46C175 48 196 69 194 100Q205 118 192 137C177 158 150 167 120 165S64 160 48 138Q35 119 46 100Z'

export function MascotHead({
  config,
  appearance: a,
  palette,
  paint,
}: {
  config: MascotConfig
  appearance: MascotAppearance
  palette: { light: string; shade: string; cream: string; pattern: string }
  paint: (name: string) => string
}) {
  const cat = config.species === 'cat'
  const jaguar = config.species === 'jaguar'
  return (
    <g className={styles.headPose}>
      <g className={styles.headMotion}>
        <g className={styles.leftEarPose}>
          <g className={styles.leftEar}>
            {jaguar ? (
              <g data-jaguar-ear="left">
                <path
                  d="M52 82C30 75 28 44 45 36c20-10 40 7 39 28Z"
                  fill="#302b24"
                  stroke={palette.shade}
                  strokeWidth="1.5"
                />
                <path d="M51 70C39 65 37 48 47 44c13-5 26 6 26 18Z" fill={paint('fur')} />
                <path d="M52 63q-9-8-4-12 9-4 17 7Z" fill="#d6a482" />
                <path
                  d="M37 49q-3 12 4 18"
                  stroke="#fff0cb"
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="none"
                />
              </g>
            ) : cat ? (
              <>
                <path
                  d="M55 86Q39 56 48 24q2-8 11-2l39 35Z"
                  fill={paint('fur')}
                  stroke={palette.shade}
                  strokeWidth="1.3"
                />
                <path d="M58 69Q49 46 54 33l28 26Z" fill="#dfaaa4" />
                <path d="m58 55 4 13 10-6" fill="#f5d2c0" />
              </>
            ) : (
              <>
                <path
                  d="M71 61Q43 39 30 64C20 85 19 125 33 142q12 13 25-3c9-18 10-41 13-78Z"
                  fill={paint('ear')}
                  stroke={palette.shade}
                  strokeWidth="1.3"
                />
                <path
                  d="M44 72q-13 26-8 51 4 14 11 4"
                  fill="none"
                  stroke={palette.light}
                  strokeWidth="7"
                  strokeLinecap="round"
                  opacity=".4"
                />
              </>
            )}
          </g>
        </g>
        <g className={styles.rightEarPose}>
          <g className={styles.rightEar}>
            {jaguar ? (
              <g data-jaguar-ear="right">
                <path
                  d="M188 82c22-7 24-38 7-46-20-10-40 7-39 28Z"
                  fill="#302b24"
                  stroke={palette.shade}
                  strokeWidth="1.5"
                />
                <path d="M189 70c12-5 14-22 4-26-13-5-26 6-26 18Z" fill={paint('fur')} />
                <path d="M188 63q9-8 4-12-9-4-17 7Z" fill="#d6a482" />
                <path
                  d="M203 49q3 12-4 18"
                  stroke="#fff0cb"
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="none"
                />
              </g>
            ) : cat ? (
              <>
                <path
                  d="M142 57 181 22q9-6 11 2 9 32-7 62Z"
                  fill={paint('fur')}
                  stroke={palette.shade}
                  strokeWidth="1.3"
                />
                <path d="m158 59 28-26q5 13-4 36Z" fill="#dfaaa4" />
                <path d="m182 55-4 13-10-6" fill="#f5d2c0" />
              </>
            ) : (
              <>
                <path
                  d="M169 61q28-22 41 3c10 21 11 61-3 78-8 9-17 7-25-3-9-18-10-41-13-78Z"
                  fill={paint('ear')}
                  stroke={palette.shade}
                  strokeWidth="1.3"
                />
                <path
                  d="M196 72q13 26 8 51-4 14-11 4"
                  fill="none"
                  stroke={palette.light}
                  strokeWidth="7"
                  strokeLinecap="round"
                  opacity=".25"
                />
              </>
            )}
          </g>
        </g>
        <path
          d={jaguar ? JAGUAR_HEAD_PATH : MASCOT_HEAD_PATH}
          fill={paint('fur')}
          stroke={palette.shade}
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <g clipPath={paint('head-clip')}>
          {config.pattern === 'patches' && (
            <path
              d="M52 53q28-22 47 4 11 21-1 41-15 15-36 1L39 80Z"
              fill={palette.pattern}
              opacity=".6"
            />
          )}
          {config.pattern === 'stripes' && (
            <g
              fill="none"
              stroke={palette.pattern}
              strokeWidth="5"
              strokeLinecap="round"
              opacity=".55"
            >
              <path d="m102 48 5 19m13-23v20m18-16-5 19M49 104l15 5m-14 8 12 3m129-16-15 5m14 8-12 1" />
            </g>
          )}
          <MascotPattern
            pattern={jaguar ? 'rosettes' : config.pattern}
            color={jaguar ? '#352b23' : palette.pattern}
            cream={palette.cream}
            face
          />
          <ellipse cx="120" cy="148" rx="61" ry="23" fill={palette.cream} opacity=".28" />
          <ellipse
            cx="120"
            cy="105"
            rx="80"
            ry="68"
            fill="#e4e6d7"
            opacity={a.sickness * 0.2}
          />
          <ellipse cx="71" cy="124" rx="24" ry="19" fill={paint('blush')} />
          <ellipse cx="169" cy="124" rx="24" ry="19" fill={paint('blush')} />
        </g>
        <path
          d="M91 66q7-8 14-7m5-3 8-3"
          fill="none"
          stroke={palette.cream}
          strokeWidth="3"
          strokeLinecap="round"
          opacity=".55"
        />
        <path
          d="m57 118 6 3m114 0 6-3"
          fill="none"
          stroke={palette.cream}
          strokeWidth="2"
          strokeLinecap="round"
          opacity=".4"
        />
        <MascotFace
          appearance={a}
          cat={cat || jaguar}
          jaguar={jaguar}
          eyeFill={paint('eye')}
        />
        <MascotDirt amount={a.dirt} face />
        <MascotHeadwear config={config} />
      </g>
    </g>
  )
}
